import React, {
  createContext,
  useState,
  useContext,
  useEffect,
} from "react";

import axios from "axios";
import CryptoJS from "crypto-js";

import config from "../config";

import {
  connectSocket,
  disconnectSocket,
} from "./socket";

const SECRET_KEY = "your-strong-secret-key";

const encryptData = (data) => {
  if (data === null || data === undefined) {
    return null;
  }

  return CryptoJS.AES.encrypt(
    JSON.stringify(data),
    SECRET_KEY
  ).toString();
};

const decryptData = (encryptedData) => {
  if (!encryptedData) {
    return null;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(
      encryptedData,
      SECRET_KEY
    );

    const decryptedString = bytes.toString(
      CryptoJS.enc.Utf8
    );

    return decryptedString
      ? JSON.parse(decryptedString)
      : null;
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] =
    useState(false);

  const [role, setRole] = useState(
    decryptData(localStorage.getItem("role"))
  );

  const [accessToken, setAccessToken] = useState(
    decryptData(localStorage.getItem("accessToken"))
  );

  const [userId, setUserId] = useState(
    decryptData(localStorage.getItem("userId"))
  );

  const [username, setUsername] = useState(
    decryptData(localStorage.getItem("username"))
  );

  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [iamLoaded, setIamLoaded] = useState(false);

  const [
    socketConnected,
    setSocketConnected,
  ] = useState(false);

  const connectToSocketIfNeeded = ({
    accessToken: token,
    userId: id,
  }) => {
    if (!token || !id) {
      return null;
    }

    try {
      const socketInstance = connectSocket({
        access_token: token,
        user_id: id,
      });

      if (!socketInstance) {
        setSocketConnected(false);
        return null;
      }

      if (socketInstance.connected) {
        setSocketConnected(true);
      } else {
        setSocketConnected(false);

        socketInstance.once("connect", () => {
          setSocketConnected(true);
        });
      }

      return socketInstance;
    } catch (error) {
      console.error(
        "Socket connection failed:",
        error
      );

      setSocketConnected(false);

      return null;
    }
  };

  const clearIam = () => {
    setPermissions([]);
    setIamLoaded(false);
  };

  const loadIamProfile = async (token) => {
    try {
      const me = await axios.get(
        `${config.apiBASEURL}/authentication/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPermissions(me.data.permissions || []);
    } catch (error) {
      console.error("IAM profile failed:", error);
      setPermissions([]);
    } finally {
      setIamLoaded(true);
    }
  };

  const checkAuthStatus = async () => {
    if (!accessToken) {
      setIsAuthenticated(false);
      setRole(null);
      setUserId(null);
      setUsername(null);
      setLoading(false);

      disconnectSocket();
      setSocketConnected(false);

      return;
    }

    try {
      const response = await axios.get(
        `${config.apiBASEURL}/authentication/status`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const {
        isAuthenticated: authenticated,
        Username,
        role: authenticatedRole,
        userId: authenticatedUserId,
      } = response.data;

      /*
       * Create the socket before authenticated
       * components are rendered.
       */
      if (authenticated && authenticatedUserId) {
        connectToSocketIfNeeded({
          accessToken,
          userId: authenticatedUserId,
        });
      }

      setRole(authenticatedRole);
      setUserId(authenticatedUserId);
      setUsername(Username);
      await loadIamProfile(accessToken);

      /*
       * Set authentication after the socket instance
       * has been created.
       */
      setIsAuthenticated(Boolean(authenticated));

      localStorage.setItem(
        "accessToken",
        encryptData(accessToken)
      );

      localStorage.setItem(
        "role",
        encryptData(authenticatedRole)
      );

      localStorage.setItem(
        "userId",
        encryptData(authenticatedUserId)
      );

      localStorage.setItem(
        "username",
        encryptData(Username)
      );
    } catch (error) {
      console.error(
        "Error checking authentication status:",
        error
      );

      setIsAuthenticated(false);
      setRole(null);
      setUserId(null);
      setUsername(null);

      disconnectSocket();
      setSocketConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();

    const intervalId = setInterval(() => {
      checkAuthStatus();
    }, 1800000);

    return () => {
      clearInterval(intervalId);
    };
  }, [accessToken]);

  const login = (authData) => {
    /*
     * Create the socket before setting
     * isAuthenticated to true.
     */
    connectToSocketIfNeeded({
      accessToken: authData.accessToken,
      userId: authData.userId,
    });

    setRole(authData.role);
    setAccessToken(authData.accessToken);
    setUserId(authData.userId);
    setUsername(authData.username);

    localStorage.setItem(
      "accessToken",
      encryptData(authData.accessToken)
    );

    localStorage.setItem(
      "role",
      encryptData(authData.role)
    );

    localStorage.setItem(
      "userId",
      encryptData(authData.userId)
    );

    localStorage.setItem(
      "username",
      encryptData(authData.username)
    );

    /*
     * Keep this last.
     */
    setIsAuthenticated(true);
    loadIamProfile(authData.accessToken);
  };

  const logout = async () => {
    disconnectSocket();

    setSocketConnected(false);
    setIsAuthenticated(false);
    setRole(null);
    setAccessToken(null);
    setUserId(null);
    setUsername(null);
    clearIam();

    localStorage.removeItem("accessToken");
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    localStorage.removeItem("username");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        role,
        accessToken,
        userId,
        username,
        permissions,
        iamLoaded,
        login,
        logout,
        loading,
        socketConnected,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);